/*
Fork addition (Stage H): keeps the page URL's `#/b/...` anchor in sync
with the board (see scripts/blueprint-anchor.ts for the format).

- After edits settle (~500ms debounce) the current plan is encoded and
  written with history.replaceState — no history entries, no reload.
  Triggers: the model's generation counters (items/links/recipes/
  counts/objectives/loads all move at least one of them) plus pointerup
  (a pure drag moves only card rects, which no counter tracks).
- Watcher writes are skipped while a hash-driven load is in flight;
  after such a load the anchor is re-synced, which canonicalises a
  pasted payload to the compressed form.
- A user-driven hashchange (pasting another `#/b/...` link into the
  same tab) loads that plan through the normal loadBlueprint path.
  replaceState never fires hashchange, so the sync cannot self-trigger.
*/
import {createGlobalState, useDebounceFn, useEventListener} from '@vueuse/core';
import {ref, watch} from 'vue';
import type {GameData} from '@/scripts/data';
import type {BlueprintModel} from '@/scripts/model/store';
import {
    BLUEPRINT_HASH_PREFIX,
    LOCAL_KEY_PREFIX,
    LOCAL_PLAN_MISSING_MESSAGE,
    LOCAL_PLAN_MISSING_TITLE,
    MAX_ANCHOR_URL_LENGTH,
    encodePlanForAnchor,
    parseBlueprintHash,
    resolveLocalPlan,
    storeLocalPlan,
} from '@/scripts/blueprint-anchor';
import {loadBlueprint} from './load-blueprint';
import {useErrorHandler} from './error-handler';

//Shared with the action dock: 'url' when the current plan travels in
//the URL itself, 'local' when it fell back to localStorage (Share then
//copies the blueprint string instead of the useless-off-device link).
export const useBlueprintAnchorState = createGlobalState(() => {
    const mode = ref<'url' | 'local'>('url');
    return {mode};
});

export function useBlueprintAnchorSync(gameData: GameData, blueprintModel: BlueprintModel) {
    const {mode} = useBlueprintAnchorState();
    const {showError} = useErrorHandler();
    let started = false;
    let loadingFromHash = false;
    let currentLocalKey = '';

    function computeAnchorHash(): string | undefined {
        const payload = encodePlanForAnchor(gameData, blueprintModel.save());
        const baseUrl = [location.protocol, '//', location.host, location.pathname].join('')
            + location.search;
        const planHash = BLUEPRINT_HASH_PREFIX + payload;
        if((baseUrl + planHash).length <= MAX_ANCHOR_URL_LENGTH) {
            mode.value = 'url';
            return planHash;
        }
        const key = storeLocalPlan(window.localStorage, payload, currentLocalKey || undefined);
        if(!key)
            return undefined;
        currentLocalKey = key;
        mode.value = 'local';
        return BLUEPRINT_HASH_PREFIX + LOCAL_KEY_PREFIX + key;
    }

    function syncNow() {
        if(!started || loadingFromHash)
            return;
        const hash = computeAnchorHash();
        if(hash && (location.hash != hash))
            history.replaceState(null, '', hash);
    }
    const debouncedSync = useDebounceFn(syncNow, 500);

    function loadFromHash() {
        const anchor = parseBlueprintHash(location.hash);
        if(!anchor)
            return;
        //already showing exactly this anchor — nothing to do
        if((anchor.kind == 'plan') && (anchor.payload == encodePlanForAnchor(gameData, blueprintModel.save())))
            return;
        if((anchor.kind == 'local') && (anchor.key == currentLocalKey))
            return;

        loadingFromHash = true;
        try {
            if(anchor.kind == 'plan') {
                loadBlueprint(gameData, blueprintModel, anchor.payload, undefined);
            } else {
                const stored = resolveLocalPlan(window.localStorage, anchor.key);
                if(stored) {
                    currentLocalKey = anchor.key;
                    loadBlueprint(gameData, blueprintModel, stored, undefined);
                } else {
                    showError(LOCAL_PLAN_MISSING_TITLE, new Error(LOCAL_PLAN_MISSING_MESSAGE));
                }
            }
        } finally {
            loadingFromHash = false;
        }
        syncNow();
    }

    function start() {
        if(started)
            return;
        started = true;
        //if the page itself was opened through a local anchor, keep
        //editing under that very key instead of minting a new one
        const initial = parseBlueprintHash(location.hash);
        if(initial?.kind == 'local')
            currentLocalKey = initial.key;
        watch(
            () => [blueprintModel.itemsGenerationNumber, blueprintModel.summaryGenerationNumber],
            debouncedSync,
        );
        useEventListener(window, 'pointerup', debouncedSync);
        useEventListener(window, 'hashchange', loadFromHash);
        syncNow();
    }

    return {start};
}
